/**
 * CountersTool.jsx — Cycle 55 · Teach & Explain
 * ──────────────────────────────────────────────
 * Two-color counters (red / yellow — standard Singapore/US convention).
 * Tap to flip. Great for negative-number "zero pairs", odd/even, and
 * simple algebraic identity work.
 *
 * State: ?n=20&s=RYRYYYYYRR   (order of counters: R or Y)
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import ManipulativeFrame from "@/components/tools/ManipulativeFrame";
import { Plus, Minus } from "lucide-react";

const clampInt = (v, lo, hi, def) => {
  const n = parseInt(v ?? "", 10);
  if (isNaN(n)) return def;
  return Math.max(lo, Math.min(hi, n));
};

export function CountersDisplay({ counters, setCounters, readOnly = false }) {
  const reds    = counters.filter((c) => c === "R").length;
  const yellows = counters.length - reds;
  const zeroPairs = Math.min(reds, yellows);
  const net = yellows - reds; // convention: yellow = +1, red = −1

  const flip = (i) => {
    if (readOnly) return;
    const next = [...counters];
    next[i] = next[i] === "R" ? "Y" : "R";
    setCounters(next);
  };

  return (
    <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 md:p-6 shadow-[4px_4px_0_0_rgba(15,23,42,1)]" data-testid="ct-canvas">
      <div className="grid grid-cols-5 md:grid-cols-10 gap-2 min-h-[100px]" data-testid="ct-grid">
        {counters.map((c, i) => (
          <button
            key={i}
            type="button"
            onClick={() => flip(i)}
            data-testid={`ct-counter-${i}`}
            aria-label={`Counter ${i + 1} is ${c === "R" ? "red" : "yellow"}`}
            className={`aspect-square rounded-full border-[3px] border-slate-900 shadow-[0_3px_0_0_rgba(15,23,42,1)] ${c === "R" ? "bg-red-400" : "bg-yellow-300"} active:translate-y-0.5 active:shadow-[0_1px_0_0_rgba(15,23,42,1)]`}
          />
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-xs font-black uppercase tracking-widest">
        <Stat tone="bg-yellow-100" label="Yellow (+1)" value={yellows} testid="ct-yellow" />
        <Stat tone="bg-red-100"    label="Red (−1)"    value={reds}    testid="ct-red" />
        <Stat tone="bg-slate-100"  label="Zero pairs"  value={zeroPairs} testid="ct-pairs" />
        <Stat tone="bg-emerald-100" label="Net value"  value={net > 0 ? `+${net}` : net} testid="ct-net" />
      </div>
    </div>
  );
}

function Stat({ tone, label, value, testid }) {
  return (
    <div className={`rounded-2xl border-[3px] border-slate-900 ${tone} py-2`} data-testid={testid}>
      <p className="text-[10px] text-slate-500">{label}</p>
      <p className="font-heading text-xl font-black text-slate-900 tabular-nums">{value}</p>
    </div>
  );
}

export default function CountersTool() {
  const [params, setParams] = useSearchParams();
  const n = clampInt(params.get("n"), 2, 60, 20);
  const seq = (params.get("s") || "").toUpperCase().replace(/[^RY]/g, "");
  const counters = Array.from({ length: n }, (_, i) => (seq[i] === "R" || seq[i] === "Y") ? seq[i] : "Y");

  const update = (patch) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) q.delete(k); else q.set(k, String(v));
    }
    setParams(q, { replace: true });
  };
  const setCounters = (next) => update({ n: next.length, s: next.join("") });

  const settingsPanel = (
    <div className="flex flex-wrap items-center gap-2">
      <label className="text-xs font-bold text-slate-700">Count
        <input type="number" min="2" max="60" value={n} data-testid="ct-count"
               onChange={(e) => update({ n: clampInt(e.target.value, 2, 60, 20), s: null })}
               className="ml-2 h-9 w-20 px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm" />
      </label>
      <button type="button" onClick={() => setCounters(counters.map(() => "Y"))} data-testid="ct-all-yellow"
              className="h-9 px-3 rounded-full border-2 border-slate-900 bg-yellow-200 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]">All yellow</button>
      <button type="button" onClick={() => setCounters(counters.map(() => "R"))} data-testid="ct-all-red"
              className="h-9 px-3 rounded-full border-2 border-slate-900 bg-red-200 font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]">All red</button>
      <button type="button"
              onClick={() => setCounters(counters.map(() => Math.random() < 0.5 ? "R" : "Y"))} data-testid="ct-shuffle"
              className="h-9 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]">Shuffle</button>
    </div>
  );

  return (
    <ManipulativeFrame
      slug="counters"
      title="Two-Colour Counters"
      description="Tap any counter to flip red↔yellow. Model number bonds, zero pairs (positive + negative), odd/even and simple algebra."
      onReset={() => setCounters(Array.from({ length: n }, () => "Y"))}
      settingsPanel={settingsPanel}
      shareUrl={window.location.href}
    >
      <CountersDisplay counters={counters} setCounters={setCounters} />
    </ManipulativeFrame>
  );
}
